import React, { useState, useCallback, useMemo } from 'react';
import Alert from '../UI/Alert';
import LoadingIndicator from '../Common/LoadingIndicator';
import Card from '../UI/Card';
import PrimaryButton from '../Common/PrimaryButton';
import PeriodoFilterButton from './PeriodoFilterButton';
import { useNotasClinicas } from '../../hooks/data/useNotasClinicas';
import { createNotaClinica, updateNotaClinica, deleteNotaClinica, assinarNotaClinica } from '../../api/api';
import EditorNotas from './EditorNotas';
import HistoricoMedicoDocumentos from './HistoricoMedicoDocumentos';
import { useNotification } from '../../context/NotificationContext';
import { useProntuarioFilter } from '../../context/ProntuarioFilterContext';
import { 
    Plus, 
    Search, 
    Edit, 
    Trash2, 
    CheckCircle2,
    Printer,
    FileText
} from 'lucide-react';
import { gerarNotaClinicaPDF, gerarNotasClinicasAgrupadasPDF } from '../../utils/gerarNotasClinicasPDF';
import Modal from '../UI/Modal';

const NotasClinicasTab = ({ pacienteId }) => {
    const { success, error: showError, warning } = useNotification();
    const prontuarioFilter = useProntuarioFilter();
    const [filters, setFilters] = useState({
        tipo: null,
        rascunho: undefined,
        search: null,
        startDate: null,
        endDate: null
    });
    // Usar o contexto compartilhado para período
    const periodoStartDate = prontuarioFilter.periodoStartDate;
    const periodoEndDate = prontuarioFilter.periodoEndDate;
    const periodo = prontuarioFilter.periodo;
    const handlePeriodoChange = prontuarioFilter.handlePeriodoChange;
    const handleDateRangeChange = prontuarioFilter.handleDateRangeChange;
    const [showEditor, setShowEditor] = useState(false);
    const [notaEditando, setNotaEditando] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
    const [showAssinarConfirm, setShowAssinarConfirm] = useState(null);

    const { data: notas, isLoading, error, refetch } = useNotasClinicas(pacienteId, {
        tipo: filters.tipo || null,
        rascunho: filters.rascunho,
        search: filters.search || null,
        startDate: periodoStartDate || filters.startDate || null,
        endDate: periodoEndDate || filters.endDate || null
    });

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };


    const handleFiltersChange = useCallback((newFilters) => {
        setFilters(prev => ({ ...prev, ...newFilters }));
    }, []);

    const handleCreateNota = useCallback(() => {
        setNotaEditando(null);
        setShowEditor(true);
    }, []);

    const handleEditNota = useCallback((nota) => {
        if (nota.bloqueado_edicao || nota.assinado) {
            warning('Esta nota não pode ser editada. Está assinada ou bloqueada.');
            return;
        }
        setNotaEditando(nota);
        setShowEditor(true);
    }, [warning]);

    const handleDeleteNota = useCallback(async (notaId) => {
        try {
            await deleteNotaClinica(pacienteId, notaId);
            success('Nota clínica removida com sucesso.');
            refetch();
            setShowDeleteConfirm(null);
        } catch (err) {
            showError(err?.response?.data?.message || 'Erro ao deletar nota.');
        }
    }, [pacienteId, refetch, success, showError]);

    const handleAssinarNota = useCallback((notaId) => {
        setShowAssinarConfirm(notaId);
    }, []);

    const confirmarAssinatura = useCallback(async () => {
        if (!showAssinarConfirm) return;
        try {
            await assinarNotaClinica(pacienteId, showAssinarConfirm);
            success('Nota clínica assinada com sucesso.');
            refetch();
            setShowAssinarConfirm(null);
        } catch (err) {
            showError(err?.response?.data?.message || 'Erro ao assinar nota.');
        }
    }, [pacienteId, showAssinarConfirm, refetch, success, showError]);

    const handleSaveNota = useCallback(async (notaData) => {
        try {
            if (notaEditando) {
                await updateNotaClinica(pacienteId, notaEditando.id, notaData);
                success('Nota clínica atualizada com sucesso.');
            } else {
                await createNotaClinica(pacienteId, notaData);
                success('Nota clínica criada com sucesso.');
            }
            refetch();
            setShowEditor(false);
            setNotaEditando(null);
        } catch (err) {
            throw err; // Re-throw para o EditorNotas tratar
        }
    }, [pacienteId, notaEditando, refetch, success]);

    // Filtrar notas por busca (filtro de data já é feito pelo hook)
    const notasFiltradas = useMemo(() => {
        if (!notas) return notas;
        // O hook já faz o filtro de data, então só precisamos filtrar por busca se necessário
        // Mas como o hook também faz busca, vamos apenas retornar as notas
        return notas;
    }, [notas]);

    const notasRascunhos = useMemo(() => notasFiltradas?.filter(n => n.rascunho) || [], [notasFiltradas]);
    const notasAssinadas = useMemo(() => notasFiltradas?.filter(n => n.assinado) || [], [notasFiltradas]);

    const handleImprimirNota = async (nota) => {
        try {
            await gerarNotaClinicaPDF(pacienteId, nota);
        } catch (err) {
            showError('Erro ao gerar PDF da nota clínica.');
        }
    };

    const handleImprimirTodas = async () => {
        if (!notasFiltradas || notasFiltradas.length === 0) {
            warning('Não há notas para imprimir.');
            return;
        }
        try {
            await gerarNotasClinicasAgrupadasPDF(pacienteId, notasFiltradas, { incluirAssinatura: true });
        } catch (err) {
            showError('Erro ao gerar PDF das notas clínicas.');
        }
    };

    return (
        <div className="space-y-4">
            {/* Cabeçalho com filtro e ações */}
            <div className="flex items-center justify-end gap-2 flex-wrap">
                <PeriodoFilterButton
                    periodo={periodo}
                    startDate={periodoStartDate}
                    endDate={periodoEndDate}
                    onPeriodoChange={handlePeriodoChange}
                    onDateRangeChange={handleDateRangeChange}
                />
                {notasFiltradas && notasFiltradas.length > 0 && (
                    <PrimaryButton
                        onClick={handleImprimirTodas}
                        variant="outline"
                        size="md"
                        className="whitespace-nowrap flex items-center gap-2"
                    >
                        <Printer className="h-4 w-4" />
                        Imprimir Todas
                    </PrimaryButton>
                )}
                <PrimaryButton onClick={handleCreateNota} className="whitespace-nowrap flex items-center gap-2">
                    <Plus className="h-4 w-4" />
                    Nova Nota
                </PrimaryButton>
            </div>

            {/* Filtros de Tipo e Status */}
            <Card>
                <div className="space-y-5 p-4 md:p-6">
                    {/* Busca */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Buscar
                        </label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Buscar em títulos, conteúdo ou tags..."
                                value={filters.search || ''}
                                onChange={(e) => handleFiltersChange({ search: e.target.value || null })}
                                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            />
                        </div>
                    </div>

                    {/* Filtros rápidos */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Filtros
                        </label>
                        <div className="flex flex-wrap gap-2.5">
                            <button
                                onClick={() => handleFiltersChange({ tipo: null, rascunho: undefined })}
                                className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                                    !filters.tipo && filters.rascunho === undefined
                                        ? 'bg-emerald-100 text-emerald-700 font-medium'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                Todas
                            </button>
                            <button
                                onClick={() => handleFiltersChange({ tipo: null, rascunho: true })}
                                className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                                    filters.rascunho === true
                                        ? 'bg-yellow-100 text-yellow-700 font-medium'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                Rascunhos ({notasRascunhos.length})
                            </button>
                            <button
                                onClick={() => handleFiltersChange({ tipo: null, rascunho: false })}
                                className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                                    filters.rascunho === false
                                        ? 'bg-green-100 text-green-700 font-medium'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                Assinadas ({notasAssinadas.length})
                            </button>
                            <button
                                onClick={() => handleFiltersChange({ tipo: 'evolucao', rascunho: undefined })}
                                className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                                    filters.tipo === 'evolucao'
                                        ? 'bg-blue-100 text-blue-700 font-medium'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                Evoluções
                            </button>
                            <button
                                onClick={() => handleFiltersChange({ tipo: 'intercorrencia', rascunho: undefined })}
                                className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                                    filters.tipo === 'intercorrencia'
                                        ? 'bg-red-100 text-red-700 font-medium'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                Intercorrências
                            </button>
                            <button
                                onClick={() => handleFiltersChange({ tipo: 'observacao', rascunho: undefined })}
                                className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                                    filters.tipo === 'observacao'
                                        ? 'bg-purple-100 text-purple-700 font-medium'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                Observações
                            </button>
                        </div>
                    </div>
                </div>
            </Card>

            {isLoading && <LoadingIndicator message="Carregando notas clínicas..." />}
            {error && <Alert type="error" message={error} />}

            {!isLoading && !error && (
                <Card>
                    {notasFiltradas && notasFiltradas.length > 0 ? (
                        <>
                            {/* Desktop */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Título</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Autor</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Documentos</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {notasFiltradas.map((nota) => (
                                            <tr key={nota.id} className={nota.rascunho ? 'bg-yellow-50' : ''}>
                                                <td className="px-4 py-2 text-sm text-gray-700">{formatDate(nota.data_registro)}</td>
                                                <td className="px-4 py-2 text-sm">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                        nota.tipo === 'evolucao' ? 'bg-blue-100 text-blue-800' :
                                                        nota.tipo === 'intercorrencia' ? 'bg-red-100 text-red-800' :
                                                        'bg-purple-100 text-purple-800'
                                                    }`}>
                                                        {nota.tipo === 'evolucao' ? 'Evolução' :
                                                         nota.tipo === 'intercorrencia' ? 'Intercorrência' :
                                                         'Observação'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-2 text-sm font-medium text-gray-900">
                                                    {nota.titulo || '(Sem título)'}
                                                </td>
                                                <td className="px-4 py-2 text-sm text-gray-700">{nota.usuario_nome || '-'}</td>
                                                <td className="px-4 py-2 text-sm">
                                                    {nota.assinado ? (
                                                        <span className="flex items-center gap-1 text-green-600">
                                                            <CheckCircle2 className="w-4 h-4" />
                                                            Assinada
                                                        </span>
                                                    ) : nota.rascunho ? (
                                                        <span className="text-yellow-600">Rascunho</span>
                                                    ) : (
                                                        <span className="text-gray-600">Pendente</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-2 text-sm">
                                                    <HistoricoMedicoDocumentos
                                                        pacienteId={pacienteId}
                                                        entidadeOrigem="notas_clinicas"
                                                        entidadeOrigemId={nota.id}
                                                        documentos={nota.documentos}
                                                        onDocumentoUploaded={refetch}
                                                    />
                                                </td>
                                                <td className="px-4 py-2 text-sm">
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => handleImprimirNota(nota)}
                                                            className="p-1 rounded text-emerald-600 hover:bg-emerald-50"
                                                            title="Imprimir nota"
                                                        >
                                                            <FileText className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleEditNota(nota)}
                                                            disabled={nota.bloqueado_edicao || nota.assinado}
                                                            className={`p-1 rounded ${
                                                                nota.bloqueado_edicao || nota.assinado
                                                                    ? 'text-gray-300 cursor-not-allowed'
                                                                    : 'text-blue-600 hover:bg-blue-50'
                                                            }`}
                                                            title={nota.bloqueado_edicao || nota.assinado ? 'Nota bloqueada' : 'Editar'}
                                                        >
                                                            <Edit className="w-4 h-4" />
                                                        </button>
                                                        {!nota.assinado && (
                                                            <button
                                                                onClick={() => handleAssinarNota(nota.id)}
                                                                className="p-1 rounded text-green-600 hover:bg-green-50"
                                                                title="Assinar nota"
                                                            >
                                                                <CheckCircle2 className="w-4 h-4" />
                                                            </button>
                                                        )}
                                                        {!nota.assinado && (
                                                            <button
                                                                onClick={() => setShowDeleteConfirm(nota.id)}
                                                                className="p-1 rounded text-red-600 hover:bg-red-50"
                                                                title="Excluir"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile */}
                            <div className="md:hidden space-y-3">
                                {notasFiltradas.map((nota) => (
                                    <div key={nota.id} className={`border rounded-lg p-3 ${nota.rascunho ? 'bg-yellow-50 border-yellow-200' : ''}`}>
                                        <div className="flex justify-between items-start mb-2">
                                            <div>
                                                <div className="font-medium text-gray-900">{nota.titulo || '(Sem título)'}</div>
                                                <div className="text-xs text-gray-500 mt-1">{formatDate(nota.data_registro)}</div>
                                            </div>
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                nota.tipo === 'evolucao' ? 'bg-blue-100 text-blue-800' :
                                                nota.tipo === 'intercorrencia' ? 'bg-red-100 text-red-800' :
                                                'bg-purple-100 text-purple-800'
                                            }`}>
                                                {nota.tipo === 'evolucao' ? 'Evolução' :
                                                 nota.tipo === 'intercorrencia' ? 'Intercorrência' :
                                                 'Observação'}
                                            </span>
                                        </div>
                                            <div className="text-sm text-gray-700 space-y-1">
                                                <div><span className="font-medium">Autor:</span> {nota.usuario_nome || '-'}</div>
                                                <div>
                                                    <span className="font-medium">Status:</span>{' '}
                                                    {nota.assinado ? (
                                                        <span className="flex items-center gap-1 text-green-600">
                                                            <CheckCircle2 className="w-3 h-3" />
                                                            Assinada
                                                        </span>
                                                    ) : nota.rascunho ? (
                                                        <span className="text-yellow-600">Rascunho</span>
                                                    ) : (
                                                        <span className="text-gray-600">Pendente</span>
                                                    )}
                                                </div>
                                                <div className="mt-2">
                                                    <span className="font-medium text-xs">Documentos:</span>
                                                    <div className="mt-1">
                                                        <HistoricoMedicoDocumentos
                                                            pacienteId={pacienteId}
                                                            entidadeOrigem="notas_clinicas"
                                                            entidadeOrigemId={nota.id}
                                                            documentos={nota.documentos}
                                                            onDocumentoUploaded={refetch}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        <div className="flex items-center gap-2 mt-3 flex-wrap">
                                            <button
                                                onClick={() => handleImprimirNota(nota)}
                                                className="px-3 py-1.5 text-sm rounded bg-emerald-50 text-emerald-600 hover:bg-emerald-100 flex items-center gap-1.5"
                                            >
                                                <FileText className="w-3 h-3" />
                                                Imprimir
                                            </button>
                                            <button
                                                onClick={() => handleEditNota(nota)}
                                                disabled={nota.bloqueado_edicao || nota.assinado}
                                                className={`px-3 py-1.5 text-sm rounded flex items-center justify-center ${
                                                    nota.bloqueado_edicao || nota.assinado
                                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                        : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                                                }`}
                                            >
                                                Editar
                                            </button>
                                            {!nota.assinado && (
                                                <button
                                                    onClick={() => handleAssinarNota(nota.id)}
                                                    className="px-3 py-1.5 text-sm rounded bg-green-50 text-green-600 hover:bg-green-100 flex items-center justify-center"
                                                >
                                                    Assinar
                                                </button>
                                            )}
                                            {!nota.assinado && (
                                                <button
                                                    onClick={() => setShowDeleteConfirm(nota.id)}
                                                    className="px-3 py-1.5 text-sm rounded bg-red-50 text-red-600 hover:bg-red-100 flex items-center justify-center"
                                                >
                                                    Excluir
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <p className="text-sm text-gray-500 p-4 text-center">
                            {filters.search ? 'Nenhuma nota encontrada para a busca.' : 'Nenhuma nota clínica registrada.'}
                        </p>
                    )}
                </Card>
            )}

            {/* Modal de Confirmação de Exclusão */}
            {showDeleteConfirm && (
                <Modal
                    isOpen={!!showDeleteConfirm}
                    onClose={() => setShowDeleteConfirm(null)}
                    title="Confirmar Exclusão"
                >
                    <div className="space-y-4">
                        <p className="text-gray-700">
                            Tem certeza que deseja excluir esta nota? Esta ação não pode ser desfeita.
                        </p>
                        <div className="flex items-center gap-3 justify-end">
                            <button
                                onClick={() => setShowDeleteConfirm(null)}
                                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 flex items-center justify-center"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => handleDeleteNota(showDeleteConfirm)}
                                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 flex items-center justify-center"
                            >
                                Excluir
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Modal de Confirmação de Assinatura */}
            {showAssinarConfirm && (
                <Modal
                    isOpen={!!showAssinarConfirm}
                    onClose={() => setShowAssinarConfirm(null)}
                    title="Confirmar Assinatura"
                >
                    <div className="space-y-4 p-1">
                        <p className="text-gray-700">
                            Tem certeza que deseja assinar esta nota? Após assinar, ela não poderá mais ser editada.
                        </p>
                        <div className="flex items-center gap-3 justify-end">
                            <button
                                onClick={() => setShowAssinarConfirm(null)}
                                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 flex items-center justify-center"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={confirmarAssinatura}
                                className="px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700 flex items-center justify-center"
                            >
                                Assinar
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Modal do Editor */}
            {showEditor && (
                <EditorNotas
                    pacienteId={pacienteId}
                    nota={notaEditando}
                    onSave={handleSaveNota}
                    onClose={() => {
                        setShowEditor(false);
                        setNotaEditando(null);
                    }}
                />
            )}
        </div>
    );
};

export default NotasClinicasTab;

